import { useState } from 'react';

interface QuotaSearchFiltersProps {
  searchTerm: string;
  onSearchChange: (term: string) => void;
  onFiltersChange: (filters: QuotaFilters) => void;
  onExportCSV: () => void;
  totalResults: number;
  loading?: boolean;
}

export interface QuotaFilters {
  planType: string;
  quotaStatus: string;
  dateRange: string;
  minQuota: string;
  maxQuota: string;
}

const defaultFilters: QuotaFilters = {
  planType: '',
  quotaStatus: '',
  dateRange: '',
  minQuota: '',
  maxQuota: ''
};

function QuotaSearchFilters({ 
  searchTerm, 
  onSearchChange, 
  onFiltersChange,
  onExportCSV,
  totalResults,
  loading = false
}: QuotaSearchFiltersProps) {
  const [filters, setFilters] = useState<QuotaFilters>(defaultFilters);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const handleFilterChange = (key: keyof QuotaFilters, value: string) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const clearAllFilters = () => {
    setFilters(defaultFilters);
    onSearchChange('');
    onFiltersChange(defaultFilters);
  };

  const hasActiveFilters = () => {
    return searchTerm || Object.values(filters).some(value => value !== '');
  };

  const getActiveFilterCount = () => {
    let count = searchTerm ? 1 : 0;
    count += Object.values(filters).filter(value => value !== '').length;
    return count;
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-4">
          <h3 className="text-lg font-medium text-gray-900">Search & Filter</h3>
          {hasActiveFilters() && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              {getActiveFilterCount()} active filter{getActiveFilterCount() !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            {showAdvanced ? 'Hide' : 'Show'} Advanced
          </button>
          <button
            onClick={onExportCSV}
            disabled={totalResults === 0 || loading}
            className="px-3 py-1 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
            </svg>
            <span>Export CSV ({totalResults})</span>
          </button>
        </div>
      </div>

      {/* Basic Search */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        {/* Search Input */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Search Users
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search by email, user ID, or plan type..."
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
          </div>
        </div>

        {/* Quick Quota Status Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Quota Status
          </label>
          <select
            value={filters.quotaStatus}
            onChange={(e) => handleFilterChange('quotaStatus', e.target.value)}
            className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          >
            <option value="">All Status</option>
            <option value="critical">Critical (&lt; 10 credits)</option>
            <option value="warning">Warning (10-49 credits)</option>
            <option value="normal">Normal (50+ credits)</option>
          </select>
        </div>
      </div>

      {/* Advanced Filters */}
      {showAdvanced && (
        <div className="border-t border-gray-200 pt-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {/* Plan Type Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Plan Type
              </label>
              <select
                value={filters.planType}
                onChange={(e) => handleFilterChange('planType', e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                <option value="">All Plans</option>
                <option value="junior_sdr">Junior SDR</option>
                <option value="top_qualifier_sdr">Top Qualifier</option>
              </select>
            </div>

            {/* Min Quota */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Min Quota
              </label>
              <input
                type="number"
                min="0"
                placeholder="0"
                value={filters.minQuota}
                onChange={(e) => handleFilterChange('minQuota', e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            {/* Max Quota */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Quota
              </label>
              <input
                type="number"
                min="0"
                placeholder="1000"
                value={filters.maxQuota}
                onChange={(e) => handleFilterChange('maxQuota', e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            {/* Date Range */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last Updated
              </label>
              <select
                value={filters.dateRange}
                onChange={(e) => handleFilterChange('dateRange', e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                <option value="">Any Time</option>
                <option value="today">Today</option>
                <option value="week">Past Week</option>
                <option value="month">Past Month</option>
                <option value="quarter">Past Quarter</option>
              </select>
            </div>

            {/* Clear Filters */}
            <div className="flex items-end">
              <button
                onClick={clearAllFilters}
                disabled={!hasActiveFilters()}
                className="w-full px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Active Filters Summary */}
      {hasActiveFilters() && (
        <div className="border-t border-gray-200 pt-4 mt-4">
          <div className="flex flex-wrap gap-2">
            <span className="text-sm text-gray-600">Active filters:</span>
            
            {searchTerm && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                Search: "{searchTerm}"
                <button
                  onClick={() => onSearchChange('')}
                  className="ml-1 text-blue-600 hover:text-blue-800"
                >
                  ×
                </button>
              </span>
            )}

            {filters.planType && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                Plan: {filters.planType === 'junior_sdr' ? 'Junior SDR' : 'Top Qualifier'}
                <button
                  onClick={() => handleFilterChange('planType', '')}
                  className="ml-1 text-purple-600 hover:text-purple-800"
                >
                  ×
                </button>
              </span>
            )}

            {filters.quotaStatus && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                Status: {filters.quotaStatus}
                <button
                  onClick={() => handleFilterChange('quotaStatus', '')}
                  className="ml-1 text-yellow-600 hover:text-yellow-800"
                >
                  ×
                </button>
              </span>
            )}

            {(filters.minQuota || filters.maxQuota) && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Quota: {filters.minQuota || '0'}-{filters.maxQuota || '∞'}
                <button
                  onClick={() => {
                    handleFilterChange('minQuota', '');
                    handleFilterChange('maxQuota', '');
                  }}
                  className="ml-1 text-green-600 hover:text-green-800"
                >
                  ×
                </button>
              </span>
            )}

            {filters.dateRange && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                Updated: {filters.dateRange}
                <button
                  onClick={() => handleFilterChange('dateRange', '')}
                  className="ml-1 text-gray-600 hover:text-gray-800"
                >
                  ×
                </button>
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export { QuotaFilters };
export default QuotaSearchFilters;